import { useCallback, useMemo } from 'react';
import { useEntityFilters } from '../ui/useEntityFilters';
import { agentMatchesSearch, searchTerms } from '@/lib/agent-search';
import { getArchivedAgents, type DraftFilters } from '@/lib/agent-utils';
import { NON_TERMINAL_STATUSES } from '@/lib/types';
import type { Agent, AgentMark, AgentStatus } from '@/lib/types';
import {
  ORIGINATOR_KIND_AGENT,
  ORIGINATOR_KIND_FLOW,
  agentDisplayAssociations,
  agentHasLabel,
  agentInGroup,
  matchesOriginatorKind,
  getGitRepoAttachment,
} from '@reflex/shared';
import type { AgentServerFilters } from './useAgents';
import { isAgentUnread, type AgentViewsMap } from './useAgentViews';
import type { Snapshot } from './useLiveAgentTurnSignals';

const STORAGE_KEY = 'agent-filters';

export type AgentStatusFilter = 'active' | 'completed' | 'error' | 'stopped' | null;
/**
 * Narrow to runs carrying a triage mark: a specific one, `marked` for any mark
 * at all (the triage inbox — "what have I flagged?"), or `null` for no mark
 * filtering. Deliberately separate from the status filter: a mark is what a
 * person concluded, status is what the runner did, and mixing them into one
 * control would make "Completed" ambiguous.
 */
export type AgentMarkFilter = AgentMark | 'marked' | null;
export type AgentTimeRangeFilter = 'today' | 'week' | 'month' | null;
/**
 * Which originator kind to show. Built-ins `agent` (user/dashboard runs and
 * their delegated subagents, the default) and `flow` (flow-launched runs),
 * plus any plugin-contributed kind keyed by id prefix (e.g. `jrr` → Jira).
 * `null` shows every kind.
 */
export type AgentOriginatorFilter = string | null;
/**
 * Which association kind an agent must carry to show: `pull_request`, a
 * plugin-contributed kind (`linear_issue`, `jira_issue`, ...), or `null` for
 * no association filtering.
 */
export type AgentAssociationKindFilter = string | null;
/**
 * Narrow to the agents filed under one group (its id), or `null` for every
 * agent. Distinct from {@link AgentAssociationKindFilter}, which asks whether
 * an agent has *any* link of a kind: this one names the specific group.
 */
export type AgentGroupFilter = string | null;
/**
 * Narrow to the agents carrying one label (its id) — the kind of work they
 * are doing — or `null` for every agent.
 */
export type AgentLabelFilter = string | null;

interface StoredFilters {
  userId?: string | null;
  status?: AgentStatusFilter;
  mark?: AgentMarkFilter;
  repoSlug?: string | null;
  timeRange?: AgentTimeRangeFilter;
  query?: string | null;
  draftsOnly?: boolean;
  originatorKind?: AgentOriginatorFilter;
  /** A specific flow run to narrow to; only meaningful when kind is `flow`. */
  flowOriginatorId?: string | null;
  associationKind?: AgentAssociationKindFilter;
  groupId?: AgentGroupFilter;
  labelId?: AgentLabelFilter;
  /**
   * Narrow to runs the viewer has not looked at since their last finished
   * turn. A per-viewer read state, so it reads the live views map + turn
   * signals passed in via the hook's options — without them the flag still
   * tracks but matches nothing.
   */
  unreadOnly?: boolean;
  /**
   * Narrow to archived runs. Off by default, where the list carries both and
   * the sidebar files the archived ones into a section of their own; on, the
   * archived runs *are* the list and the server paginates over them, so an
   * archived run older than the loaded pages is reachable at all.
   */
  archivedOnly?: boolean;
}

const COMPLETED_STATUSES: AgentStatus[] = ['completed'];
const ERROR_STATUSES: AgentStatus[] = ['error'];
const STOPPED_STATUSES: AgentStatus[] = ['stopping', 'stopped', 'terminated'];

const STATUS_GROUP: Record<string, AgentStatus[]> = {
  active: [...NON_TERMINAL_STATUSES],
  completed: COMPLETED_STATUSES,
  error: ERROR_STATUSES,
  stopped: STOPPED_STATUSES,
};

export interface AgentFilters {
  userId: string | null;
  status: AgentStatusFilter;
  mark: AgentMarkFilter;
  repoSlug: string | null;
  timeRange: AgentTimeRangeFilter;
  query: string | null;
  draftsOnly: boolean;
  originatorKind: AgentOriginatorFilter;
  flowOriginatorId: string | null;
  associationKind: AgentAssociationKindFilter;
  groupId: AgentGroupFilter;
  labelId: AgentLabelFilter;
  unreadOnly: boolean;
  archivedOnly: boolean;
}

/**
 * The unfiltered state of every filter except the free-text query, which the
 * search box owns. Single source of truth for both "clear all" and the active
 * count on the filter trigger, so adding a filter cannot leave one of them
 * behind: clearing writes these values, and a filter counts as active exactly
 * when it differs from them.
 */
const UNFILTERED: Omit<AgentFilters, 'query'> = {
  userId: null,
  status: null,
  mark: null,
  repoSlug: null,
  timeRange: null,
  draftsOnly: false,
  originatorKind: null,
  flowOriginatorId: null,
  associationKind: null,
  groupId: null,
  labelId: null,
  unreadOnly: false,
  archivedOnly: false,
};

/**
 * Project the agent filters onto the ones a draft can answer.
 *
 * Owner, search, time range, and repo carry over; everything else lands in
 * `runOnly`, which hides drafts outright while it is set. The originator kind
 * is a special case: a draft is a plain user launch, so the default `agent`
 * kind keeps it and any other kind (flow, Jira, ...) hides it.
 */
export function draftFiltersFrom(filters: AgentFilters): DraftFilters {
  return {
    query: filters.query,
    timeRange: filters.timeRange,
    userId: filters.userId,
    repoSlug: filters.repoSlug,
    runOnly: [
      filters.status,
      filters.mark,
      filters.originatorKind === ORIGINATOR_KIND_AGENT ? null : filters.originatorKind,
      filters.associationKind,
      filters.groupId,
      filters.labelId,
      // A draft has no per-viewer read state, so the unread filter is a no-op
      // on it except to hide it (the same way status/mark hide drafts). A
      // string sentinel rather than the boolean so the `runOnly` slot stays
      // string-typed.
      filters.unreadOnly ? 'unread' : null,
      // A draft is never archived, so asking for the archived list hides them
      // the same way a status or a mark does.
      filters.archivedOnly ? 'archived' : null,
    ],
  };
}

/**
 * Project the filters onto the facets `GET /agents` applies itself, so the
 * list paginates over the matching rows instead of over the most recent page.
 *
 * Only the facets that are a plain predicate on a run belong here. The rest
 * (repo, association kind, originator, free text, time range, unread) stay
 * client-side and are converged by {@link useAgentListCompletion} — they
 * depend on hydrated attachments, per-viewer read state, or a locale-relative
 * cutoff, none of which the route can answer today.
 *
 * `filterAgents` still re-applies every facet on render, including these. The
 * duplication is deliberate: it keeps one definition of "this run matches" for
 * rows that never came from the list query (a pinned run, a search hit, an
 * optimistic insert), and it is free — the predicate is already running.
 */
export function serverAgentFilters(filters: AgentFilters): AgentServerFilters {
  return {
    userId: filters.userId,
    // An unrecognised group means "no status narrowing", not "match nothing":
    // a stored value from an older build must not empty the list.
    statuses: filters.status ? (STATUS_GROUP[filters.status] ?? null) : null,
    // `marked` is the UI's word for "carries any mark"; the wire calls it `any`.
    mark: filters.mark === 'marked' ? 'any' : filters.mark,
    groupId: filters.groupId,
    labelId: filters.labelId,
    // Only ever narrows *to* the archived runs. Off, the facet stays unset
    // rather than sending `archived=false`, because the default list still
    // wants the archived rows for the sidebar's archived section.
    archived: filters.archivedOnly ? true : null,
  };
}

export interface AgentFiltersResult {
  filters: AgentFilters;
  /**
   * {@link serverAgentFilters} for the current filters, stable across renders
   * that do not change them. Pass to `useAgents` so its pages are already
   * narrowed.
   */
  serverFilters: AgentServerFilters;
  /**
   * Whether a facet the server does not apply is currently narrowing the
   * list, so what survives on screen is a filtered view of the loaded pages
   * rather than of the org. Drives {@link useAgentListCompletion}.
   *
   * Measured against this hook's own defaults, not against "unfiltered": the
   * sidebar defaults to the Agent originator kind and the monitor to all
   * kinds, and a default is not a reason to start pulling pages.
   */
  clientFilterActive: boolean;
  currentUserId: string | null;
  setUserId: (id: string | null) => void;
  setStatus: (status: AgentStatusFilter) => void;
  setMark: (mark: AgentMarkFilter) => void;
  setRepoFullName: (name: string | null) => void;
  setTimeRange: (timeRange: AgentTimeRangeFilter) => void;
  setQuery: (query: string | null) => void;
  setDraftsOnly: (draftsOnly: boolean) => void;
  setOriginatorKind: (kind: AgentOriginatorFilter) => void;
  setFlowOriginatorId: (id: string | null) => void;
  setAssociationKind: (kind: AgentAssociationKindFilter) => void;
  setGroupId: (groupId: AgentGroupFilter) => void;
  setLabelId: (labelId: AgentLabelFilter) => void;
  setUnreadOnly: (unreadOnly: boolean) => void;
  setArchivedOnly: (archivedOnly: boolean) => void;
  /**
   * Drop every filter, including the ones that default to on (owner, type).
   * "Clear all" means an unfiltered list, not "back to the first-visit
   * defaults" — so each field is persisted as an explicit {@link UNFILTERED}
   * value rather than removed, which would let the defaults reapply. The
   * search query is left alone: it lives in its own box, with its own clear
   * button.
   */
  clearFilters: () => void;
  filterAgents: (agents: Agent[]) => Agent[];
}

export interface UseAgentFiltersOptions {
  /** Override the sessionStorage key (default: 'agent-filters'). */
  storageKey?: string;
  /** Override the default userId filter (default: currentUserId). Pass `null` to default to all users. */
  defaultUserId?: string | null;
  /**
   * Override the default originator kind (default: `agent`, i.e. user/dashboard
   * runs and their delegated subagents). Pass `null` to default to all kinds —
   * e.g. the admin monitor, which should surface flow- and integration-launched
   * runs too.
   */
  defaultOriginatorKind?: AgentOriginatorFilter;
  /**
   * Live per-viewer read state the `unreadOnly` filter narrows by. Without it,
   * the flag still tracks and renders a chip, but `unreadOnly` matches nothing
   * (no agent is considered unread). Thread both into every list that shares
   * the filter chip so the facet and the filtering agree.
   */
  unreadViews?: AgentViewsMap;
  /** The shared live turn-signal snapshot backing {@link unreadViews}. */
  unreadActivities?: Snapshot;
}

const getAgentStatus = (a: Agent): AgentStatus => a.status;
const getStatusFilter = (f: AgentFilters): string | null => f.status;

function normalizeAgentFilters(stored: StoredFilters, defaults: StoredFilters): AgentFilters {
  return {
    userId: stored.userId === undefined ? (defaults.userId ?? null) : (stored.userId ?? null),
    status: stored.status ?? null,
    mark: stored.mark ?? null,
    repoSlug: stored.repoSlug ?? null,
    timeRange: stored.timeRange ?? null,
    query: stored.query ?? null,
    draftsOnly: stored.draftsOnly ?? false,
    // Undefined (first visit) falls back to the default kind (agent-only);
    // an explicit stored null means the user chose "All types".
    originatorKind:
      stored.originatorKind === undefined
        ? (defaults.originatorKind ?? null)
        : stored.originatorKind,
    flowOriginatorId: stored.flowOriginatorId ?? null,
    associationKind: stored.associationKind ?? null,
    groupId: stored.groupId ?? null,
    labelId: stored.labelId ?? null,
    unreadOnly: stored.unreadOnly ?? false,
    archivedOnly: stored.archivedOnly ?? false,
  };
}

function agentExtraFilter(agents: Agent[], filters: AgentFilters): Agent[] {
  let result = agents;

  // Archived: re-applied here for the rows that never came from the list query
  // — a pinned run, a search hit, an optimistic insert — which the server's
  // `archived=true` page never saw.
  if (filters.archivedOnly) {
    result = getArchivedAgents(result);
  }

  // Triage mark: a specific mark, or `marked` for "anything I have flagged".
  if (filters.mark) {
    result =
      filters.mark === 'marked'
        ? result.filter((a) => !!a.mark)
        : result.filter((a) => a.mark === filters.mark);
  }

  // Originator kind: keep only agents whose `originator` classifies to the
  // selected kind (null = all kinds). Within `flow`, an optional specific
  // flow-run id narrows further.
  if (filters.originatorKind) {
    result = result.filter((a) => matchesOriginatorKind(a.originator, filters.originatorKind!));
    if (filters.originatorKind === ORIGINATOR_KIND_FLOW && filters.flowOriginatorId) {
      result = result.filter((a) => a.originator === filters.flowOriginatorId);
    }
  }

  if (filters.repoSlug) {
    result = result.filter((a) => getGitRepoAttachment(a)?.repoSlug === filters.repoSlug);
  }

  // Association kind: keep only agents linked to at least one work item of
  // the selected kind (a PR, a Linear/Jira issue, ...). Runs over the
  // display refs so the legacy single-prUrl fallback still counts as a PR.
  if (filters.associationKind) {
    result = result.filter((a) =>
      agentDisplayAssociations(a).some((ref) => ref.kind === filters.associationKind),
    );
  }

  // Group: keep only agents filed under the selected group. Membership is an
  // association, so this reads the same hydrated data the chips render from.
  if (filters.groupId) {
    result = result.filter((a) => agentInGroup(a, filters.groupId!));
  }

  // Label: keep only agents carrying the selected label. Same shape as the
  // group filter — both read the memberships hydrated onto the agent.
  if (filters.labelId) {
    result = result.filter((a) => agentHasLabel(a, filters.labelId!));
  }

  // Free text matches everything the row puts on screen (title, agent type,
  // model, repo, branch, linked PRs/issues), not just the title and prompt.
  const terms = searchTerms(filters.query);
  if (terms.length > 0) {
    result = result.filter((a) => agentMatchesSearch(a, terms));
  }

  if (filters.timeRange) {
    const now = new Date();
    let cutoff: number;
    if (filters.timeRange === 'today') {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      cutoff = start.getTime();
    } else if (filters.timeRange === 'week') {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
      cutoff = start.getTime();
    } else {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      cutoff = start.getTime();
    }
    result = result.filter((a) => a.createdAt >= cutoff);
  }

  return result;
}

/**
 * Per-tab agent filters.
 *
 * The filters (owner, status, repo, time range, drafts, and the search query)
 * live in `sessionStorage`, so they persist across a reload but stay private to
 * each tab: typing a search or flipping a filter in one tab never mirrors into
 * another tab on the same origin. They reset when the tab is closed.
 *
 * On first visit (no stored value), `userId` defaults to `currentUserId`
 * unless overridden via `options.defaultUserId`.
 */
export function useAgentFilters(
  currentUserId: string | null,
  options?: UseAgentFiltersOptions,
): AgentFiltersResult {
  const key = options?.storageKey ?? STORAGE_KEY;
  const unreadViews = options?.unreadViews;
  const unreadActivities = options?.unreadActivities;
  const defaultUid = options?.defaultUserId !== undefined ? options.defaultUserId : currentUserId;
  const defaultKind =
    options?.defaultOriginatorKind !== undefined
      ? options.defaultOriginatorKind
      : ORIGINATOR_KIND_AGENT;

  const defaults: StoredFilters = useMemo(
    () => ({
      userId: defaultUid,
      status: null,
      mark: null,
      repoSlug: null,
      timeRange: null,
      query: null,
      draftsOnly: false,
      // Default to the Agent kind: user/dashboard runs and their delegated
      // children. Flow and plugin-launched runs stay hidden until the user
      // picks their kind (or "All").
      originatorKind: defaultKind,
      flowOriginatorId: null,
      associationKind: null,
      groupId: null,
      labelId: null,
      unreadOnly: false,
      archivedOnly: false,
    }),
    [defaultUid, defaultKind],
  );

  // The static facets (mark, originator, repo, association, group, label,
  // query, time range) are a pure function of the stored filters, so they live
  // in the module-level `agentExtraFilter`. The unread facet also depends on
  // the viewer's live read state, so it is applied here in a closure that
  // recreates only when that state changes — keeping `filterItems` stable
  // across renders that merely flip an unrelated filter. When `unreadOnly` is
  // off (the default) the closure is the module-level filter unchanged.
  //
  // With the flag on but no live context threaded in, `isAgentUnread` returns
  // false for every run, so the facet narrows to nothing rather than silently
  // showing all runs as "unread" — every real caller passes the context.
  const extraFilter = useCallback(
    (items: Agent[], f: AgentFilters): Agent[] => {
      const filtered = agentExtraFilter(items, f);
      if (!f.unreadOnly) return filtered;
      return filtered.filter((a) => isAgentUnread(unreadViews, unreadActivities, a));
    },
    [unreadViews, unreadActivities],
  );

  const { filters, setField, setFields, filterItems } = useEntityFilters<
    StoredFilters,
    AgentFilters,
    Agent,
    AgentStatus
  >({
    storageKey: key,
    storageKind: 'session',
    defaults,
    normalize: normalizeAgentFilters,
    statusGroupMap: STATUS_GROUP,
    getStatusFilter: getStatusFilter,
    getItemStatus: getAgentStatus,
    extraFilter,
  });

  const serverFilters = useMemo(() => serverAgentFilters(filters), [filters]);

  const clientFilterActive = useMemo(
    () =>
      filters.repoSlug !== null ||
      filters.associationKind !== null ||
      filters.query !== null ||
      filters.timeRange !== null ||
      filters.flowOriginatorId !== null ||
      filters.unreadOnly ||
      filters.originatorKind !== (defaults.originatorKind ?? null),
    [filters, defaults],
  );

  const setUserId = useCallback((id: string | null) => setField('userId', id), [setField]);

  const setStatus = useCallback(
    (status: AgentStatusFilter) => setField('status', status),
    [setField],
  );

  const setMark = useCallback((mark: AgentMarkFilter) => setField('mark', mark), [setField]);

  const setRepoFullName = useCallback(
    (name: string | null) => setField('repoSlug', name),
    [setField],
  );

  const setTimeRange = useCallback(
    (timeRange: AgentTimeRangeFilter) => setField('timeRange', timeRange),
    [setField],
  );

  const setQuery = useCallback(
    (query: string | null) => setField('query', query && query.length > 0 ? query : null),
    [setField],
  );

  const setDraftsOnly = useCallback(
    (draftsOnly: boolean) => setField('draftsOnly', draftsOnly),
    [setField],
  );

  const clearFilters = useCallback(() => setFields(UNFILTERED), [setFields]);

  const setOriginatorKind = useCallback(
    (kind: AgentOriginatorFilter) => {
      setField('originatorKind', kind);
      // The specific-flow filter only applies within the flow kind; drop it
      // whenever we leave that kind so it can't silently constrain other views.
      if (kind !== ORIGINATOR_KIND_FLOW) setField('flowOriginatorId', null);
    },
    [setField],
  );

  const setFlowOriginatorId = useCallback(
    (id: string | null) => setField('flowOriginatorId', id),
    [setField],
  );

  const setAssociationKind = useCallback(
    (kind: AgentAssociationKindFilter) => setField('associationKind', kind),
    [setField],
  );

  const setGroupId = useCallback(
    (groupId: AgentGroupFilter) => setField('groupId', groupId),
    [setField],
  );

  const setLabelId = useCallback(
    (labelId: AgentLabelFilter) => setField('labelId', labelId),
    [setField],
  );

  const setUnreadOnly = useCallback(
    (unreadOnly: boolean) => setField('unreadOnly', unreadOnly),
    [setField],
  );

  const setArchivedOnly = useCallback(
    (archivedOnly: boolean) => setField('archivedOnly', archivedOnly),
    [setField],
  );

  return {
    filters,
    serverFilters,
    clientFilterActive,
    currentUserId,
    setUserId,
    setStatus,
    setMark,
    setRepoFullName,
    setTimeRange,
    setQuery,
    setDraftsOnly,
    setOriginatorKind,
    setFlowOriginatorId,
    setAssociationKind,
    setGroupId,
    setLabelId,
    setUnreadOnly,
    setArchivedOnly,
    clearFilters,
    filterAgents: filterItems,
  };
}
